/* ============================================================
   FileUpload — drag & drop STL/OBJ/3MF upload zone
   ============================================================ */

import { useCallback, useRef, useState } from 'react';
import { api } from '../api/client';
import type { FileStatus, ModelFileResponse } from '../types';

const ACCEPTED_FORMATS = ['.stl', '.obj', '.3mf', '.step'];
const MAX_SIZE = 150 * 1024 * 1024; // 150 MB

interface FileUploadProps {
  onUploaded: (file: ModelFileResponse) => void;
}

type UploadState = 'idle' | 'uploading' | 'analyzing' | 'done' | 'error';

const isAnalyzedStatus = (status: FileStatus) => status === 'Analyzed' || status === 2;
const isFailedStatus = (status: FileStatus) => status === 'Failed' || status === 3;

export function FileUpload({ onUploaded }: FileUploadProps) {
  const [dragOver, setDragOver] = useState(false);
  const [state, setState] = useState<UploadState>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    async (file: File) => {
      setErrorMsg(null);
      setFileName(file.name);

      // Validate extension
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!ACCEPTED_FORMATS.includes(ext)) {
        setErrorMsg(`Формат ${ext} не поддерживается. Допустимо: STL, OBJ, 3MF, STEP`);
        setState('error');
        return;
      }

      // Validate size
      if (file.size > MAX_SIZE) {
        setErrorMsg('Файл слишком большой. Максимум 150 МБ.');
        setState('error');
        return;
      }

      setState('uploading');
      try {
        const result = await api.uploadModel(file);
        setState('analyzing');

        // Poll until the backend reaches a terminal analysis state.
        // Staging can be slow on first runs, so do not fail only because a model takes longer than expected.
        const poll = async () => {
          while (true) {
            await new Promise((r) => setTimeout(r, 2500));
            const updated = await api.getModel(result.id);
            if (isAnalyzedStatus(updated.status)) {
              setState('done');
              onUploaded(updated);
              return;
            }
            if (isFailedStatus(updated.status)) {
              setState('error');
              setErrorMsg('Не удалось проанализировать файл: ' + (updated.analysisWarnings ?? 'ошибка'));
              return;
            }
          }
        };

        // If already analyzed immediately
        if (isAnalyzedStatus(result.status)) {
          setState('done');
          onUploaded(result);
        } else {
          poll();
        }
      } catch (err) {
        setState('error');
        setErrorMsg(err instanceof Error ? err.message : 'Ошибка загрузки');
      }
    },
    [onUploaded],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const isWorking = state === 'uploading' || state === 'analyzing';

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      onClick={() => !isWorking && inputRef.current?.click()}
      style={{
        border: '2px dashed',
        borderColor: dragOver
          ? 'var(--color-primary)'
          : state === 'done'
            ? 'var(--color-success)'
            : 'var(--color-separator)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--space-2xl) var(--space-xl)',
        textAlign: 'center',
        cursor: isWorking ? 'default' : 'pointer',
        transition: 'border-color var(--transition-fast), background var(--transition-fast)',
        background: dragOver ? 'rgba(255, 149, 0, 0.04)' : 'var(--color-surface)',
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_FORMATS.join(',')}
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
        }}
      />

      {state === 'idle' && (
        <>
          <UploadIcon />
          <p style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)', marginTop: 'var(--space-md)' }}>
            Перетащите файл сюда
          </p>
          <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)' }}>
            STL, OBJ, 3MF, STEP · до 150 МБ
          </p>
        </>
      )}

      {isWorking && (
        <>
          <div className="spinner" style={{ margin: '0 auto' }} />
          <p style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-medium)', marginTop: 'var(--space-md)' }}>
            {state === 'uploading' ? 'Загрузка...' : 'Анализ модели...'}
          </p>
          {fileName && (
            <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)' }}>
              {fileName}
            </p>
          )}
        </>
      )}

      {state === 'done' && (
        <>
          <CheckIcon />
          <p style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)', marginTop: 'var(--space-md)', color: 'var(--color-success)' }}>
            Модель загружена
          </p>
          {fileName && (
            <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)' }}>
              {fileName}
            </p>
          )}
        </>
      )}

      {state === 'error' && (
        <>
          <ErrorIcon />
          <p style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)', marginTop: 'var(--space-md)', color: 'var(--color-danger)' }}>
            Ошибка
          </p>
          {errorMsg && (
            <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)' }}>
              {errorMsg}
            </p>
          )}
        </>
      )}
    </div>
  );
}

/* ── Icons ────────────────────────────────────────────────── */

function UploadIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" style={{ opacity: 0.5 }}>
      <path
        d="M24 6L34 16M24 6L14 16M24 6V32M8 32V38C8 39.1 8.9 40 10 40H38C39.1 40 40 39.1 40 38V32"
        stroke="var(--color-text-secondary)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
      <circle cx="24" cy="24" r="20" fill="rgba(52, 199, 89, 0.12)" />
      <path d="M16 24L22 30L32 18" stroke="var(--color-success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
      <circle cx="24" cy="24" r="20" fill="rgba(255, 59, 48, 0.12)" />
      <path d="M24 14V28M24 32V34" stroke="var(--color-danger)" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}
